import { field, label } from '../contacts/Sheet.tsx';
import { useAssignees, useFollowUpAssignees, type FollowUp, type Task, type TaskObject } from '../../api/taskQueries.ts';
import { OBJECT_WORDS, followUpOptions, followUpTarget } from '../../tasks/taskView.ts';
import { DueField, DUE_NONE, dueToSend, type DueValue } from './Fields.tsx';

/**
 * The follow-up a completion or an action may raise (the contract's FollowUp): to the reader by default. Completing a
 * task (taskId given), the chooser is the server's list for that task (151, GET /tasks/:id/follow-up-assignees):
 * anyone assigned to the client in any department, each director, colleagues and the reader, each under the
 * department the follow-up is made in. Recording an action has no task yet, so it keeps the department's list.
 */
export function FollowUpFields({ department, taskId, about, today, value, onChange }: {
  department: Task['department']; taskId?: string; about?: TaskObject | null; today: string;
  value: { on: boolean; title: string; due: DueValue; to: string };
  onChange: (v: { on: boolean; title: string; due: DueValue; to: string }) => void;
}) {
  const people = useAssignees(value.on && !taskId ? department : null, 'follow_up');
  const listed = useFollowUpAssignees(value.on && taskId ? taskId : null);
  const options = taskId
    ? followUpOptions((listed.data?.people ?? []).filter((p) => p.why !== 'you'))
    : (people.data?.people ?? []).map((p) => ({ value: p.id, label: p.name }));
  return (
    <fieldset className="mt-3 rounded-lg border border-neutral-300 px-3 py-2">
      <legend className="px-1 text-sm font-semibold text-neutral-800">Follow-up</legend>
      <label className="flex min-h-[44px] items-center gap-2 text-neutral-900">
        <input type="checkbox" className="size-5" checked={value.on} onChange={(e) => onChange({ ...value, on: e.target.checked })} />
        Add a follow-up task
      </label>
      {value.on && (
        <div className="space-y-3">
          <label className={label}>What to do next
            <input type="text" maxLength={200} value={value.title} onChange={(e) => onChange({ ...value, title: e.target.value })} className={field} />
          </label>
          {about && (
            <p className="text-neutral-900"><span className={label}>{OBJECT_WORDS[about.kind]}</span>{about.label ?? OBJECT_WORDS[about.kind]} (the same as this task's)</p>
          )}
          <DueField value={value.due} onChange={(due) => onChange({ ...value, due })} today={today} />
          <label className={label}>For
            <select value={value.to} onChange={(e) => onChange({ ...value, to: e.target.value })} className={field}>
              <option value="">Me</option>
              {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
        </div>
      )}
    </fieldset>
  );
}

export const NO_FOLLOW_UP = { on: false, title: '', due: { ...DUE_NONE, choice: 'tomorrow' as const }, to: '' };

/** The follow-up to send, `undefined` for none, or null when it is on but not yet complete. */
export function followUpToSend(v: typeof NO_FOLLOW_UP | { on: boolean; title: string; due: DueValue; to: string }, today: string): FollowUp | undefined | null {
  if (!v.on) return undefined;
  const due = dueToSend(v.due, today);
  if (!v.title.trim() || !due) return null;
  return { title: v.title.trim(), due: due.spec, ...followUpTarget(v.to) };
}

/** Today as the agency's calendar day, New York: the default and the bound of every date the forms preview. */
export function agencyToday(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

